'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Partner {
  id: number;
  name: string;
  title: string;
  company: string;
  location: string;
  expertise: string[];
  image: string;
  alt: string;
  certifications: string[];
}

const NetworkDirectorySection = () => {
  const [selectedExpertise, setSelectedExpertise] = useState<string>('All');

  const expertiseFilters = [
  'All',
  'AI Strategy',
  'Change Management',
  'Digital Transformation',
  'Organizational Design',
  'Leadership Development'];


  const partners: Partner[] = [
  {
    id: 1,
    name: 'David Richardson',
    title: 'Senior AI Transformation Consultant',
    company: 'Strategic Futures Ltd',
    location: 'London, UK',
    expertise: ['AI Strategy', 'Change Management', 'Leadership Development'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15a1404b5-1763299032692.png",
    alt: 'Professional man with short dark hair in navy suit smiling in modern office',
    certifications: ['AI Change Leader', 'Certified Change Practitioner']
  },
  {
    id: 2,
    name: 'Rachel Foster',
    title: 'Principal Consultant',
    company: 'Innovation Partners',
    location: 'Manchester, UK',
    expertise: ['Digital Transformation', 'Organizational Design', 'AI Strategy'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18d4825d1-1763293841178.png",
    alt: 'Professional woman with shoulder-length brown hair in burgundy blazer smiling confidently',
    certifications: ['AI Change Leader', 'Agile Transformation Coach']
  },
  {
    id: 3,
    name: 'Michael O\'Brien',
    title: 'Managing Director',
    company: 'Enterprise Evolution',
    location: 'Edinburgh, UK',
    expertise: ['Change Management', 'Leadership Development', 'AI Strategy'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0ea71ee-1763293839923.png",
    alt: 'Professional man with beard in gray suit smiling warmly in corporate setting',
    certifications: ['AI Change Leader', 'Executive Coach']
  },
  {
    id: 4,
    name: 'Priya Sharma',
    title: 'Lead Transformation Consultant',
    company: 'Digital Catalyst Group',
    location: 'Birmingham, UK',
    expertise: ['AI Strategy', 'Digital Transformation', 'Change Management'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16d92d732-1763296764294.png",
    alt: 'Professional woman with long dark hair in white blouse smiling in bright office',
    certifications: ['AI Change Leader', 'Lean Six Sigma Black Belt']
  },
  {
    id: 5,
    name: 'Thomas Wright',
    title: 'Senior Partner',
    company: 'Transform Consulting',
    location: 'Bristol, UK',
    expertise: ['Organizational Design', 'Leadership Development', 'Change Management'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18bcbcac9-1763295749722.png",
    alt: 'Professional man with glasses in charcoal suit smiling in executive office',
    certifications: ['AI Change Leader', 'Organizational Development Specialist']
  },
  {
    id: 6,
    name: 'Sophie Anderson',
    title: 'Founder & Principal',
    company: 'Future Ready Partners',
    location: 'Leeds, UK',
    expertise: ['AI Strategy', 'Change Management', 'Digital Transformation'],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_138023b5c-1763301844903.png",
    alt: 'Professional woman with blonde hair in navy blazer smiling confidently in modern workspace',
    certifications: ['AI Change Leader', 'Digital Strategy Expert']
  }];


  const filteredPartners = selectedExpertise === 'All' ?
  partners :
  partners.filter((partner) => partner.expertise.includes(selectedExpertise));

  return (
    <section className="py-20 lg:py-32 bg-card">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-bold font-headline text-primary mb-6">
            Meet Our Partner Network
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Connect with senior consultants and boutique firms delivering AI transformations across industries.
          </p>
        </div>
        
        <div className="mb-8 flex flex-wrap gap-3 justify-center">
          {expertiseFilters.map((filter) =>
          <button
            key={filter}
            onClick={() => setSelectedExpertise(filter)}
            className={`px-4 py-2 rounded-lg font-medium font-body text-sm transition-all duration-300 ${
            selectedExpertise === filter ?
            'bg-secondary text-secondary-foreground shadow-subtle' :
            'bg-background text-foreground hover:bg-muted border border-border'}`
            }>

              {filter}
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {filteredPartners.map((partner) =>
          <div
            key={partner.id}
            className="bg-background rounded-xl shadow-card hover:shadow-modal transition-all duration-300 hover:-translate-y-1 overflow-hidden border border-border">

              <div className="relative w-full aspect-square overflow-hidden">
                <AppImage
                src={partner.image}
                alt={partner.alt}
                className="w-full h-full object-cover" />

              </div>
              
              <div className="p-6">
                <h3 className="text-xl font-bold font-headline text-primary mb-1">
                  {partner.name}
                </h3>
                <p className="text-sm text-muted-foreground font-body mb-1">
                  {partner.title}
                </p>
                <p className="text-sm text-secondary font-semibold font-body mb-3">
                  {partner.company}
                </p>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
                  <Icon name="MapPinIcon" size={16} variant="solid" />
                  <span>{partner.location}</span>
                </div>
                
                <div className="mb-4">
                  <div className="text-xs font-semibold text-foreground font-body mb-2">
                    Expertise:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {partner.expertise.map((exp, idx) =>
                  <span
                    key={idx}
                    className="px-2 py-1 bg-primary/5 text-primary text-xs rounded font-body">

                        {exp}
                      </span>
                  )}
                  </div>
                </div>
                
                <div>
                  <div className="text-xs font-semibold text-foreground font-body mb-2">
                    Certifications:
                  </div>
                  <div className="space-y-1">
                    {partner.certifications.map((cert, idx) =>
                  <div key={idx} className="flex items-center gap-2">
                        <Icon name="CheckBadgeIcon" size={16} variant="solid" className="text-success" />
                        <span className="text-xs text-foreground font-body">{cert}</span>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-12 text-center">
          <p className="text-muted-foreground font-body mb-4">
            Showing {filteredPartners.length} of {partners.length} partners
          </p>
          <p className="text-sm text-muted-foreground font-body">
            All partners are certified AI Change Leaders with proven track records in enterprise transformation.
          </p>
        </div>
      </div>
    </section>);

};

export default NetworkDirectorySection;